import React, {useMemo, useState} from "react";
import NiceModal from "@ebay/nice-modal-react";
import SyncIcon from "@mui/icons-material/Sync";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {GridColDef} from "@mui/x-data-grid";
import {useNavigate} from "react-router-dom";
import {toast} from "sonner";
import {Box, Chip, Colors, Text} from "@bounce/shared/ui";
import {Button, ListDataGrid, ListPageHeader, RowActions, SearchInput} from "../../ui/components";
import AreYouSureDialog from "@bounce/shared/consumer/bounce-ui/components/Dialogs/AreYouSureDialog";
import DeleteResourceDialog from "../../components/DeleteResourceDialog";
import {useGetSnippets} from "../../apis/requests/snippets/getSnippets";
import {useSyncSnippetFromCio} from "../../apis/requests/snippets/syncSnippetFromCio";
import {ReachSnippet} from "../../apis/requests/snippets/types";
import {FlagES, FlagUS} from "../../assets";
import {reachUrls} from "../../layout/menu-items";
import {SNIPPET_SOURCE_DISPLAY} from "./snippetDisplay";
import {snippetUrl} from "./helpers";

const SnippetsPage = () => {
  const navigate = useNavigate();
  const {data, isLoading, error} = useGetSnippets();
  const {mutate: syncOneSnippet} = useSyncSnippetFromCio();
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({page: 0, pageSize: 25});

  const filteredSnippets = useMemo(() => {
    const all: ReachSnippet[] = data ?? [];
    const q = search.trim().toLowerCase();
    const matchesSearch = (snippet: ReachSnippet) =>
      !q ||
      snippet.name.toLowerCase().includes(q) ||
      (snippet.element_name ?? "").toLowerCase().includes(q) ||
      snippet.body.toLowerCase().includes(q);
    return all.filter(matchesSearch);
  }, [data, search]);

  const onDelete = (snippet: ReachSnippet) => {
    NiceModal.show(DeleteResourceDialog, {
      kind: "snippet",
      resourceId: snippet.id,
      resourceName: snippet.name,
    });
  };

  const onReplace = (snippet: ReachSnippet) => {
    NiceModal.show(DeleteResourceDialog, {
      kind: "snippet",
      mode: "replace",
      resourceId: snippet.id,
      resourceName: snippet.name,
    });
  };

  const onSyncOne = (snippet: ReachSnippet) => {
    NiceModal.show(AreYouSureDialog, {
      onConfirm: () =>
        syncOneSnippet(
          {id: snippet.id},
          {onSuccess: () => toast.success(`Snippet "${snippet.name}" synced from Customer.io`)}
        ),
      headerTextId: "Sync from Customer.io?",
      contentTextId: `This replaces the HTML of "${snippet.name}" with the current version in Customer.io.`,
      confirmTextId: "Sync",
    });
  };

  const columns = useMemo<GridColDef<ReachSnippet>[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 2,
        renderCell: ({row}) => (
          <Text
            size={14}
            variant="inter400"
            sx={{color: Colors.grey._900, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
          >
            {row.name}
          </Text>
        ),
      },
      {
        field: "element_name",
        headerName: "Element",
        flex: 2,
        renderCell: ({row}) =>
          row.element_name ? (
            <Text size={13} sx={{color: Colors.grey._700, fontFamily: "monospace"}}>
              {row.element_name}
            </Text>
          ) : null,
      },
      {
        field: "source",
        headerName: "Source",
        flex: 1.5,
        renderCell: ({row}) => {
          const display = SNIPPET_SOURCE_DISPLAY[row.source];
          return <Chip size_="small" variant_={display.variant} label={display.label} />;
        },
      },
      {
        field: "language",
        headerName: "Language",
        flex: 1,
        valueGetter: (_value, row) => row.language ?? "en",
        renderCell: ({row}) => <Box component={row.language === "es" ? FlagES : FlagUS} sx={{width: 18, height: 18}} />,
      },
      {
        field: "principal",
        headerName: "Created by",
        flex: 1,
        filterable: false,
        renderCell: ({row}) => (
          <Text
            size={14}
            sx={{color: Colors.grey._700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
          >
            {row.principal ? row.principal.split("@")[0] : ""}
          </Text>
        ),
      },
      {
        field: "actions",
        headerName: "",
        width: 72,
        sortable: false,
        filterable: false,
        renderCell: ({row}) => (
          <RowActions
            onEdit={() => navigate(snippetUrl(row.id))}
            onDelete={() => onDelete(row)}
            onOpenInNewTab={() => window.open(snippetUrl(row.id), "_blank")}
            extraActions={[
              {
                label: "Replace everywhere",
                icon: <SwapHorizIcon sx={{width: 20, height: 20}} />,
                onClick: () => onReplace(row),
              },
              ...(row.source === "manual"
                ? []
                : [
                    {
                      label: "Sync from Customer.io",
                      icon: <SyncIcon sx={{width: 20, height: 20}} />,
                      onClick: () => onSyncOne(row),
                    },
                  ]),
            ]}
          />
        ),
      },
    ],
    [navigate]
  );

  return (
    <Box sx={{p: 4, display: "flex", flexDirection: "column", height: "100%"}}>
      <ListPageHeader title="Snippets">
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPaginationModel((m) => ({...m, page: 0}));
          }}
          placeholder="Search snippets..."
        />
        <Button buttonType="contained-primary" size="small" onClick={() => navigate(reachUrls.snippetsNew)}>
          + New snippet
        </Button>
      </ListPageHeader>
      {error ? (
        <Text size={14}>Failed to load snippets.</Text>
      ) : (
        <ListDataGrid
          rows={filteredSnippets}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          initialState={{sorting: {sortModel: [{field: "name", sort: "asc"}]}}}
          getRowUrl={(row) => snippetUrl(row.id)}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableColumnMenu={false}
          noRowsText={search.trim() ? "No snippets match your search" : "No snippets yet"}
        />
      )}
    </Box>
  );
};

export default SnippetsPage;
